import { Component, inject, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ToastService } from '@src/app/core/helpers/toast.service';
import { ToastEvent } from '@src/app/shared/interfaces/toast-event';
import { EventTypes } from '@src/app/shared/interfaces/event-types';
import { Subscription } from 'rxjs';

interface Toast extends ToastEvent {
  id: number;
}

@Component({
  selector: 'app-toast-container',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <div class="toast-container">
      <div *ngFor="let toast of toasts()" [ngClass]="['toast-unique', toast.type]">
        <ng-container *ngIf="toast.type === 'err'; else normalToast">
          <div class="toast-err-inner">
            <strong class="toast-err-title">{{ toast.title }}</strong>
            <pre class="toast-err-body">{{ toast.message }}</pre>
            <button
              type="button"
              class="toast-copy-btn"
              (click)="copyErrorToast(toast, $event)"
            >
              Copiar erro
            </button>
          </div>
        </ng-container>
        <ng-template #normalToast>
          <strong class="toast-title">{{ toast.title }}</strong>
          <div class="toast-message">{{ toast.message }}</div>
          <ng-container *ngIf="toast.routerLink">
            <a class="toast-link" [routerLink]="toast.routerLink">{{ toast.routerLinkLabel || toast.routerLink }}</a>
          </ng-container>
        </ng-template>
        <button type="button" class="close-btn" (click)="removeToast(toast.id)">&times;</button>
      </div>
    </div>
  `,
  styleUrl: './toast-container.component.scss'
})
export class ToastContainerComponent implements OnDestroy {
  private toastService = inject(ToastService);
  toasts = signal<Toast[]>([]);
  private sub: Subscription;
  private toastId = 0;
  private readonly MAX_TOASTS = 3;

  constructor() {
    this.sub = this.toastService.toastEvents.subscribe((event: ToastEvent) => {
      const newToast: Toast = { ...event, id: this.toastId++ };

      this.toasts.update(currentToasts => {
        const newToasts = [...currentToasts, newToast];
        if (newToasts.length > this.MAX_TOASTS) {
          return newToasts.slice(newToasts.length - this.MAX_TOASTS);
        }
        return newToasts;
      });

      if (!event.infinite) {
        const ms = event.type === EventTypes.isError ? 15000 : 4000;
        setTimeout(() => this.removeToast(newToast.id), ms);
      }
    });
  }

  removeToast (id: number) {
    this.toasts.update(currentToasts => currentToasts.filter(toast => toast.id !== id));
  }

  copyErrorToast(toast: Toast, ev: Event): void {
    ev.stopPropagation();
    const text = `${toast.title}\n\n${toast.message}`;
    void navigator.clipboard.writeText(text);
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
